/**
 * Staff ops surfaces: Loans, Clients, Reconciliation, Audit Trail, Regions.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Play, Send } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ExportButton } from "@/components/staff/ExportButton";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/lendingCalculations";
import { staffApi } from "@/platform/staffApi";
import { statusBadge } from "./WorkPages";

type Row = Record<string, any>;

function useList(loader: () => Promise<Row[]>) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    loader().then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  }, [loader]);
  useEffect(load, [load]);
  return { rows, loading, load };
}

const Spinner = () => <Loader2 className="mx-auto my-8 h-6 w-6 animate-spin" />;

// ============ Loans ============

export function LoansPage() {
  const { toast } = useToast();
  const { rows, loading, load } = useList(
    useCallback(() => staffApi.loans("?limit=200").then((d) => d.loans), []),
  );
  const [busy, setBusy] = useState<string | null>(null);

  const disburse = async (loanId: string) => {
    setBusy(loanId);
    try {
      await staffApi.disburse(loanId);
      toast({ title: "Disbursement submitted", description: "Money is on its way to the wallet." });
      load();
    } catch (error) {
      toast({ title: "Disbursement failed", description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Loans</h1>
          <p className="text-sm text-muted-foreground">The active book in your regions.</p>
        </div>
        <ExportButton dataset="loans" />
      </header>
      <Card><CardContent className="pt-4">
        {loading ? <Spinner /> : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No loans yet.</p>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Loan</TableHead>
              <TableHead>Principal</TableHead><TableHead>Outstanding</TableHead><TableHead>Monthly</TableHead>
              <TableHead>Term</TableHead><TableHead>Status</TableHead><TableHead>Disbursed</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>
                    <Link to={`/staff/loans/${l.id}`} className="font-mono text-xs text-primary hover:underline">
                      {String(l.id).slice(-8)}
                    </Link>
                  </TableCell>
                  <TableCell className="tabular-nums">{formatCurrency(l.principal_amount)}</TableCell>
                  <TableCell className="font-medium tabular-nums">{formatCurrency(l.outstanding_balance)}</TableCell>
                  <TableCell className="tabular-nums">{formatCurrency(l.monthly_payment)}</TableCell>
                  <TableCell>{l.term_months} mo</TableCell>
                  <TableCell>{statusBadge(l.status)}</TableCell>
                  <TableCell className="text-sm">{l.disbursement_date || "not yet"}</TableCell>
                  <TableCell className="text-right">
                    {!l.disbursement_date && l.status === "active" && (
                      <Button size="sm" disabled={busy === l.id} onClick={() => disburse(l.id)}>
                        {busy === l.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1 h-4 w-4" />Disburse</>}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </div>
  );
}

// ============ Clients ============

export function ClientsPage() {
  const [search, setSearch] = useState("");
  const { rows, loading, load } = useList(
    useCallback(() => staffApi.customers(search ? `?search=${encodeURIComponent(search)}` : "").then((d) => d.customers), [search]),
  );

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Clients</h1>
          <p className="text-sm text-muted-foreground">Customers in your regions. Searches are audited.</p>
        </div>
        <ExportButton dataset="customers"
          params={search ? `?search=${encodeURIComponent(search)}` : ""} />
      </header>
      <div className="flex gap-2">
        <Input placeholder="Search name, phone, or NRC" value={search}
          onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="max-w-sm" />
        <Button variant="outline" onClick={load}>Search</Button>
      </div>
      <Card><CardContent className="pt-4">
        {loading ? <Spinner /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>NRC</TableHead>
              <TableHead>KYC</TableHead><TableHead>Plan</TableHead><TableHead>Joined</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">
                    <Link to={`/staff/clients/${c.id}`} className="text-primary hover:underline">
                      {c.full_name || "(no name)"}
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm">{c.phone}</TableCell>
                  <TableCell className="text-sm">{c.nrc_number || "—"}</TableCell>
                  <TableCell>{statusBadge(c.kyc_status)}</TableCell>
                  <TableCell className="text-sm capitalize">{c.plan_tier}</TableCell>
                  <TableCell className="text-sm">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </div>
  );
}

// ============ Reconciliation ============

export function ReconciliationPage() {
  const { toast } = useToast();
  const runs = useList(useCallback(() => staffApi.reconRuns().then((d) => d.runs), []));
  const items = useList(useCallback(() => staffApi.reconItems().then((d) => d.items), []));
  const unmatched = useList(useCallback(() => staffApi.unmatched().then((d) => d.unmatched), []));

  const trigger = async () => {
    try {
      await staffApi.reconTrigger();
      toast({ title: "Reconciliation run complete" });
      runs.load(); items.load();
    } catch (error) {
      toast({ title: "Run failed", description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    }
  };

  const resolve = async (kind: "item" | "unmatched", id: string) => {
    const note = window.prompt("Resolution note (required):");
    if (!note?.trim()) return;
    try {
      if (kind === "item") await staffApi.resolveItem(id, "resolved", note.trim());
      else await staffApi.resolveUnmatched(id, "written_off", note.trim());
      toast({ title: "Resolved" });
      items.load(); unmatched.load();
    } catch (error) {
      toast({ title: "Failed", description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Reconciliation</h1>
          <p className="text-sm text-muted-foreground">Daily provider match, exceptions, and unmatched money.</p>
        </div>
        <Button onClick={trigger}><Play className="mr-1 h-4 w-4" />Run now</Button>
      </header>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Recent runs</CardTitle></CardHeader>
          <CardContent>
            {runs.loading ? <Spinner /> : runs.rows.length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">No runs yet — trigger one after payment activity.</p>
            ) : runs.rows.map((r) => (
              <div key={r.id} className="flex items-center justify-between border-b py-2 text-sm last:border-0">
                <span>{r.run_date}</span>
                <span className="tabular-nums text-muted-foreground">{r.matched} matched</span>
                <span className={Number(r.exceptions) > 0 ? "font-medium text-warning" : "text-success"}>
                  {r.exceptions} exceptions
                </span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Open exceptions</CardTitle></CardHeader>
          <CardContent>
            {items.loading ? <Spinner /> : items.rows.length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">No open exceptions — the books agree.</p>
            ) : items.rows.map((i) => (
              <div key={i.id} className="flex items-center justify-between border-b py-2 text-sm last:border-0">
                <div>
                  <p className="font-medium">{String(i.kind).split("_").join(" ")}</p>
                  <p className="text-xs text-muted-foreground">{i.reference || i.loan_id || "—"}</p>
                </div>
                <div className="text-right tabular-nums">
                  <p>{i.our_amount != null ? formatCurrency(i.our_amount) : "—"} / {i.their_amount != null ? formatCurrency(i.their_amount) : "—"}</p>
                  <Button size="sm" variant="link" className="h-auto p-0" onClick={() => resolve("item", i.id)}>resolve</Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Unmatched payments</CardTitle></CardHeader>
        <CardContent>
          {unmatched.loading ? <Spinner /> : unmatched.rows.length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground">No unattributed money — every kwacha has a home.</p>
          ) : unmatched.rows.map((u) => (
            <div key={u.id} className="flex items-center justify-between border-b py-2 text-sm last:border-0">
              <span>{u.provider_reference || "?"} · {u.msisdn || "unknown number"}</span>
              <span className="tabular-nums">{u.amount != null ? formatCurrency(u.amount) : "—"}</span>
              <Button size="sm" variant="link" onClick={() => resolve("unmatched", u.id)}>resolve</Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ============ Audit Trail ============

export function AuditPage() {
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const { rows, loading, load } = useList(
    useCallback(() => {
      const params = new URLSearchParams();
      if (actor) params.set("actor_id", actor);
      if (action) params.set("action", action);
      return staffApi.auditEvents(params.size ? `?${params}` : "").then((d) => d.events);
    }, [actor, action]),
  );

  const auditParams = (() => {
    const p = new URLSearchParams();
    if (actor) p.set("actor_id", actor);
    if (action) p.set("action", action);
    return p.toString() ? `?${p}` : "";
  })();

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Audit Trail</h1>
          <p className="text-sm text-muted-foreground">Who did what, who viewed what. Append-only — reading it is also recorded.</p>
        </div>
        <ExportButton dataset="audit" params={auditParams} />
      </header>
      <div className="flex gap-2">
        <Input placeholder="Actor id" value={actor} onChange={(e) => setActor(e.target.value)} className="max-w-xs" />
        <Input placeholder="Action (view, approve, denied…)" value={action} onChange={(e) => setAction(e.target.value)} className="max-w-xs" />
        <Button variant="outline" onClick={load}>Filter</Button>
      </div>
      <Card><CardContent className="pt-4">
        {loading ? <Spinner /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>When</TableHead><TableHead>Actor</TableHead><TableHead>Action</TableHead>
              <TableHead>Entity</TableHead><TableHead>From</TableHead><TableHead>Change</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="whitespace-nowrap text-xs">{e.created_at ? new Date(e.created_at).toLocaleString() : "—"}</TableCell>
                  <TableCell className="text-xs">{e.actor_role || "system"}<br /><span className="text-muted-foreground">{String(e.actor_id || "").slice(0, 8)}</span></TableCell>
                  <TableCell>{statusBadge(e.action)}</TableCell>
                  <TableCell className="text-xs">{e.entity_type}<br /><span className="text-muted-foreground">{String(e.entity_id || "").slice(0, 12)}</span></TableCell>
                  {/* Where the action came from — the first thing you want in
                      a fraud or dispute investigation. */}
                  <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">
                    {e.ip || "—"}
                  </TableCell>
                  <TableCell className="max-w-md text-xs text-muted-foreground">
                    {e.before || e.after
                      ? `${JSON.stringify(e.before ?? {})} → ${JSON.stringify(e.after ?? {})}`
                      : e.meta ? JSON.stringify(e.meta) : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </div>
  );
}
